// The stanzas chosen for a song in the Liturgy Planner, shown after the song the way bulletins do:
// '517 - All Hail the Power of Jesus' Name!  vv. 1–3, 6' (issue #4169)
import { SongListEntry } from './model.ts';
import { isBound } from './model_util.ts';

// what separates the song from its suffix in a cell and on the clipboard
const suffixSeparator = '  ';

/** The highest verse number a song list entry may hold: the three digits a pasted suffix may name */
export const maxVerse = 999;

// the song, whitespace, then 'v.' or 'vv.' and a list of verses or ranges ('1–3, 6', '1-3,6', '2 — 4') at the very end
const suffixPattern =
  /^(.*\S)\s+vv?\.\s*(\d{1,3}(?:\s*[-–—]\s*\d{1,3})?(?:\s*,\s*\d{1,3}(?:\s*[-–—]\s*\d{1,3})?)*)\s*$/i;
const rangeSeparatorPattern = /\s*[-–—]\s*/;

/** Positive integers only, deduplicated and ascending; undefined when nothing is left */
export function normalizeVerses(verses: readonly number[] | undefined): number[] | undefined {
  const normalized = [...new Set(verses?.filter((verse) => Number.isInteger(verse) && verse >= 1))].sort(
    (a, b) => a - b,
  );
  return normalized.length > 0 ? normalized : undefined;
}

/**
 * 'vv. 1–3, 6' for [1, 2, 3, 6] and 'v. 4' for [4]: runs collapse to a range, the rest are comma separated; '' for no
 * verses
 */
export function formatVerses(verses: readonly number[] | undefined): string {
  const normalized = normalizeVerses(verses);
  if (!normalized) {
    return '';
  }
  const parts: string[] = [];
  let start = normalized[0];
  let end = start;
  for (const verse of [...normalized.slice(1), undefined]) {
    if (verse === end + 1) {
      end = verse;
      continue;
    }
    parts.push(start === end ? `${start}` : `${start}–${end}`);
    if (verse !== undefined) {
      start = verse;
      end = verse;
    }
  }
  return `${normalized.length === 1 ? 'v.' : 'vv.'} ${parts.join(', ')}`;
}

/** The suffix a song list entry shows after its song, undefined when no verses were chosen or it holds no song */
export function getVersesSuffix(entry: SongListEntry | undefined): string | undefined {
  return isBound(entry) ? formatVerses(entry.verses) || undefined : undefined;
}

/**
 * Whether two entries show the same in a cell, whatever their rows: two songs that are the same song with the same
 * verses as getVersesSuffix shows them (so [3, 1] matches [1, 3], and no verses matches []), or two texts that are the
 * same text, two blanks included. A song never matches a text. Unlike isSameSong, which ignores the verses
 */
export function isSameShownEntry(lhs: SongListEntry, rhs: SongListEntry): boolean {
  if (isBound(lhs) || isBound(rhs)) {
    return isBound(lhs) && isBound(rhs) && lhs.slug === rhs.slug && getVersesSuffix(lhs) === getVersesSuffix(rhs);
  }
  return lhs.text === rhs.text;
}

/** The inverse of appendVersesSuffix: the text without its verses suffix, and the verses it named */
export function splitVersesSuffix(text: string): { text: string; verses?: number[] } {
  const match = suffixPattern.exec(text);
  if (!match) {
    return { text };
  }
  const [, name, list] = match;
  const verses: number[] = [];
  for (const item of list.split(',')) {
    const [first, last = first] = item.trim().split(rangeSeparatorPattern).map(Number);
    if (first < 1 || last < 1) {
      return { text };
    }
    for (let verse = Math.min(first, last); verse <= Math.max(first, last); verse++) {
      verses.push(verse);
    }
  }
  return { text: name.trim(), verses: normalizeVerses(verses) };
}

/** The text followed by its verses suffix, as a cell reads and as it is copied; the text alone without verses */
export function appendVersesSuffix(text: string, verses: readonly number[] | undefined): string {
  const suffix = formatVerses(verses);
  return suffix ? `${text}${suffixSeparator}${suffix}` : text;
}

/**
 * The verses as stored and shown: normalized, or undefined when there are none or they are every stanza of a song
 * whose stanza count is known, since every stanza is the default and needs no note (issue #4169). A count that is not
 * a positive integer is unknown, and then the normalized verses are kept whatever they are.
 */
export function getPartialVerses(
  verses: readonly number[] | undefined,
  verseCount: number | undefined,
): number[] | undefined {
  const normalized = normalizeVerses(verses);
  if (!normalized || verseCount === undefined || !Number.isInteger(verseCount) || verseCount < 1) {
    return normalized;
  }
  const isEveryVerse = normalized.length === verseCount && normalized.every((verse, index) => verse === index + 1);
  return isEveryVerse ? undefined : normalized;
}

/**
 * The verses the Church tab shows after a song (issue #4169): the entry's choice, or undefined when it holds no song,
 * no verses were chosen, or the choice is every stanza (getPartialVerses; lists saved before the picker stored every
 * stanza as no verses still hold them). The count is the manifest's verseCount: the Church tab never fetches the
 * song file for it.
 */
export function getVersesToShow(
  entry: SongListEntry | undefined,
  verseCount: number | undefined,
): number[] | undefined {
  return isBound(entry) ? getPartialVerses(entry.verses, verseCount) : undefined;
}
